import React from 'react';
import { styled, Media } from 'themes';
import { IoIosBriefcase } from 'react-icons/io';
import Avatar from './avatar';
import NameAgeDisplay from './name-age-display';
import StarRating from './star-rating';
import {
  Payload,
  PublicActivityItem,
  TaskCompletedPayload,
  TaskBookedPayload,
  YepSearchStartedPayload,
} from './types';
import { useTranslation } from 'react-i18next';

export enum Type {
  TASK_BOOKED = 'task-booked',
  TASK_COMPLETED = 'task-completed',
  YEP_SEARCH_STARTED = 'yep-search-started',
  YEP_FIRST_TASK = 'yep-first-task',
  YEP_REACHED_LEVEL = 'yep-reached-level',
  // YEP_OF_THE_MONTH = 'yep-of-the-month',
  SHARE = 'share',
  STATISTIC = 'statistic',
}

const ICON_MAP = {
  IoIosBriefcase: IoIosBriefcase,
};

export const createTypedTranslate = (
  type: Type,
  payload: Payload,
  translate: Translate
): Translate => {
  let base = `component--public-activity-item.${type}`;
  if (payload.isCompany) {
    base = `${base}-isCompany`;
  }

  return (modifier, args) => translate(`${base}.${modifier}`, args);
};

interface ListItemProps<T> {
  onClick: () => void;
  payload: T;
  translate: Translate;
  typedTranslate: Translate;
  å;
}

interface GenericWrapperProps {
  onClick: () => void;
  image: string;
  children: React.ReactNode;
}

const GenericWrapper = (props: GenericWrapperProps) => {
  const { onClick, image, children } = props;
  return (
    <GenericWrapperContainer image={image} onClick={onClick}>
      {children}
    </GenericWrapperContainer>
  );
};

const GenericWrapperContainer = styled.button`
  display: block;
  position: relative;
  max-width: 260px;
  min-width: 260px;
  height: 140px;
  margin-right: 15px;
  margin-bottom: 15px;

  background-image: linear-gradient(241deg, rgba(43, 44, 61, 0.2), #2b2c3d),
    ${({ image }: { image: string }) => `url(${image})`};
  background-size: cover;
  border-radius: 3px;
  overflow: hidden;
  width: 100%;
  text-align: left;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(1);
  }

  ${Media.laptopL} {
    margin-right: 0px;
  }
`;

interface GenericTaskLikeComponentProps {
  onClick: () => void;
  image: string;

  smallerIconName?: string;
  subtitle?: string;
  title?: string;
  name?: string;
  age?: number;
  avatar?: string;
  rating?: number;
}
const GenericTaskLikeComponent = (props: GenericTaskLikeComponentProps) => {
  const {
    smallerIconName,

    subtitle,
    title,
    name,
    age,
    avatar,
    rating,
  } = props;

  const IconComponent = smallerIconName ? ICON_MAP[smallerIconName] : null;

  return (
    <GenericWrapper {...props}>
      <LayoutWrapper>
        {Boolean(subtitle) && (
          <Subtitle>
            {Boolean(smallerIconName) && (
              <>
                <IconComponent />
                {'  '}
              </>
            )}
            {subtitle}
          </Subtitle>
        )}
        {Boolean(title) && <Title>{title}</Title>}
        {Boolean((name && age) || rating || avatar) && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {Boolean(avatar) && <Avatar src={avatar} size={[26]} mr={[2]} />}
            <div>
              {Boolean(name && age) && (
                <NameAgeDisplay age={age!} name={name!} />
              )}
              {Boolean(rating) && <StarRating rating={rating!} size={10} />}
            </div>
          </div>
        )}
      </LayoutWrapper>
    </GenericWrapper>
  );
};

const LayoutWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 15px;
`;

const Title = styled.p`
  color: ${({ theme }) => theme.colors.white};
  font-weight: 700;
  font-size: 1.8rem;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.white};
  font-size: 1rem;
  margin-bottom: 1px;
`;

const YepSearchStartedComponent = (
  props: ListItemProps<YepSearchStartedPayload>
) => {
  const {
    payload,
    payload: { image, isCompany },
    typedTranslate,
  } = props;

  return (
    <GenericTaskLikeComponent
      {...props}
      image={image}
      smallerIconName={isCompany ? 'IoIosBriefcase' : ''}
      subtitle={typedTranslate('subtitle', payload)}
      title={typedTranslate('title', payload)}
    />
  );
};

const TaskBookedComponent = (props: ListItemProps<TaskBookedPayload>) => {
  const {
    payload,
    payload: { image, yepAge, yepFirstName, yepAvatar, isCompany },
    typedTranslate,
  } = props;

  return (
    <GenericTaskLikeComponent
      {...props}
      image={image}
      smallerIconName={isCompany ? 'IoIosBriefcase' : ''}
      subtitle={typedTranslate('subtitle', payload)}
      title={typedTranslate('title', payload)}
      avatar={yepAvatar}
      name={yepFirstName}
      age={yepAge}
    />
  );
};

const TaskCompletedComponent = (props: ListItemProps<TaskCompletedPayload>) => {
  const {
    payload,
    payload: { image, yepAge, yepFirstName, yepAvatar, rating, isCompany },
    typedTranslate,
  } = props;

  return (
    <GenericTaskLikeComponent
      {...props}
      image={image}
      smallerIconName={isCompany ? 'IoIosBriefcase' : ''}
      subtitle={typedTranslate('subtitle', payload)}
      title={typedTranslate('title', payload)}
      avatar={yepAvatar}
      name={yepFirstName}
      age={yepAge}
      rating={rating}
    />
  );
};

const COMPONENT_MAP: {
  [key in Type]?: React.ComponentType<ListItemProps<any>>;
} = {
  // [Type.TASK_BOOKED]: TaskBookedComponent,
  [Type.TASK_COMPLETED]: TaskCompletedComponent,
  // [Type.YEP_SEARCH_STARTED]: YepSearchStartedComponent,
  // [Type.SHARE]: ShareComponent,
  // [Type.STATISTIC]: StatisticComponent,
  // [Type.YEP_REACHED_LEVEL]: YepReachedLevelComponent,
  // [Type.YEP_FIRST_TASK]: YepFirstTaskComponent,
};

const _getComponentForItem = (item: PublicActivityItem) => {
  return COMPONENT_MAP[item.type];
};

interface Props {
  item: PublicActivityItem;
}

type EnhancedProps = {
  onClick: () => void;
} & Props;

const ActivityItem = (props: EnhancedProps) => {
  const { t } = useTranslation();
  const { item, onClick } = props;

  const type = item.type;
  const payload = item.payload;
  const typedTranslate = createTypedTranslate(type, payload, t);

  const Component = _getComponentForItem(item);

  if (Component) {
    return (
      <>
        <Component
          onClick={onClick}
          // style={style}
          // onClick={handlePress}
          // onSharePress={onSharePress}
          // onCTAPress={onCTAPress}
          payload={payload}
          translate={t}
          typedTranslate={typedTranslate}
        />
      </>
    );
  }

  return null;
};

export default ActivityItem;
